import { axiosAuthBackgroundInstance } from "src/boot/AxiosInstances";
import { EventLabel, EventSignup, listEventSignupLabels } from "src/composables/InleagueApiV1.Event";
import { ReactiveReifiedPromise } from "src/helpers/ReifiedPromise";
import { arrayDouble, assertTruthy, exhaustiveCaseGuard, FK_nodeRef, forceCheckedIndexedAccess, lazyRef, noAvailableOptions, parseIntOrFail, possessivify, requireNonNull, sortBy, UiOption, UiOptions, vReqT } from "src/helpers/utils";
import { Competition, Guid, Integerlike } from "src/interfaces/InleagueApiV1";
import { computed, defineComponent, onMounted, ref } from "vue";
import { SoccerBall } from "../SVGs";
import { FormKit, FormKitMessages } from "@formkit/vue";
import * as R_EventLabels from "./EventLabels.route";
import { RouterLink } from "vue-router";
import { Btn2, btn2_redEnabledClasses } from "../UserInterface/Btn2";
import { Client } from "src/store/Client";
import { getCompetitionsOrFail } from "src/store/Competitions";
import * as R_SeasonCompetitionAdmin from "src/components/Admin/SeasonCompetitions/pages/R_AdminPage.route"
import { MutUiSelection } from "../GameScheduler/calendar/PracticeSchedulerActions";

export const AddEventSignupLabelsModal = defineComponent({
  props: {
    eventID: vReqT<Guid>(),
    eventSignups: vReqT<EventSignup[]>(),
  },
  emits: {
    submit: (_: {eventLabelIDs: Integerlike[]}) => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    const selectedEventLabelIDs = ref<Integerlike[]>([])
    const labelOptionsResolver = ReactiveReifiedPromise<UiOption<Integerlike>[]>(
      async () => {
        return await listEventSignupLabels(axiosAuthBackgroundInstance, {eventID: props.eventID})
          .then(vs => vs.sort(sortBy(v => v.label)))
          .then(vs => vs.map(v => {
            return {
              label: v.label,
              value: v.eventSignupLabelID
            }
          }))
      }
    )

    const submit = () => {
      ctx.emit("submit", {eventLabelIDs: selectedEventLabelIDs.value})
    }

    const cancel = () => {
      ctx.emit("cancel")
    }

    const fname = (v: EventSignup) => {
      return v.userFirstName || v.childFirstName
    }
    const lname = (v: EventSignup) => {
      return v.userLastName || v.childLastName
    }

    return () => {
      return <div>
        <div>
          {props.eventSignups.length === 1
            ? `Add the following labels to ${possessivify(fname(props.eventSignups[0]) + " " + lname(props.eventSignups[0]))} signup:`
            : `Add the following labels to ${props.eventSignups.length} signups:`}
        </div>
        {(() => {
          switch (labelOptionsResolver.underlying.status) {
            case "idle": return null
            case "error": return <div class="p-2">Sorry, something went wrong.</div>
            case "pending": return <div class="flex p-2 items-center gap-2"><SoccerBall/>Loading...</div>
            case "resolved": {
              const options = labelOptionsResolver.underlying.data
              const ManageLabelsLink = () => <RouterLink class="il-link" to={{name: R_EventLabels.RouteName}}>Manage labels here</RouterLink>

              return <div>
                {options.length === 0
                  ? <div>
                    <p>No labels found.</p>
                    <ManageLabelsLink/>
                  </div>
                  : <div>
                    <FormKit type="checkbox" options={options} v-model={selectedEventLabelIDs.value}/>
                    <ManageLabelsLink/>
                    <div class="flex gap-2 items-center mt-2">
                      <Btn2 class="px-2 py-1" onClick={submit}>Submit</Btn2>
                      <Btn2 class="px-2 py-1" onClick={cancel} enabledClasses={btn2_redEnabledClasses}>Cancel</Btn2>
                    </div>
                  </div>}
              </div>
            }
            default: exhaustiveCaseGuard(labelOptionsResolver.underlying)
          }
        })()}
      </div>

    }
  }
})

export const RemoveEventSignupLabelsModal = defineComponent({
  props: {
    eventID: vReqT<Guid>(),
    eventSignups: vReqT<EventSignup[]>(),
  },
  emits: {
    submit: (_: {eventLabelIDs: Integerlike[]}) => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    const selectedEventLabelIDs = ref<Integerlike[]>([])
    const labelOptionsResolver = ReactiveReifiedPromise<UiOption<Integerlike>[]>(
      async () => {
        return await listEventSignupLabels(axiosAuthBackgroundInstance, {eventID: props.eventID})
          .then(vs => vs.sort(sortBy(v => v.label)))
          .then(vs => vs.map(v => {
            return {
              label: v.label,
              value: v.eventSignupLabelID
            }
          }))
      }
    )

    const submit = () => {
      ctx.emit("submit", {eventLabelIDs: selectedEventLabelIDs.value})
    }

    const cancel = () => {
      ctx.emit("cancel")
    }

    return () => {
      return <div>
        <div>Remove the following labels from the selected event signups:</div>
        {(() => {
          switch (labelOptionsResolver.underlying.status) {
            case "idle": return null
            case "error": return <div class="p-2">Sorry, something went wrong.</div>
            case "pending": return <div class="flex p-2 items-center gap-2"><SoccerBall/>Loading...</div>
            case "resolved": {
              const options = labelOptionsResolver.underlying.data
              const ManageLabelsLink = () => <RouterLink class="il-link" to={{name: R_EventLabels.RouteName}}>Manage labels here</RouterLink>

              return <div>
                {options.length === 0
                  ? <div>
                    <p>No labels found.</p>
                    <ManageLabelsLink/>
                  </div>
                  : <div>
                    <FormKit type="checkbox" options={options} v-model={selectedEventLabelIDs.value}/>
                    <ManageLabelsLink/>
                    <div class="flex gap-2 items-center mt-2">
                      <Btn2 class="px-2 py-1" onClick={submit}>Submit</Btn2>
                      <Btn2 class="px-2 py-1" onClick={cancel} enabledClasses={btn2_redEnabledClasses}>Cancel</Btn2>
                    </div>
                  </div>}
              </div>
            }
            default: exhaustiveCaseGuard(labelOptionsResolver.underlying)
          }
        })()}
      </div>

    }
  }
})

export const UpdateEventSignupLabelsModal = defineComponent({
  props: {
    eventID: vReqT<Guid>(),
    eventSignup: vReqT<EventSignup>(),
  },
  emits: {
    submit: (_: {eventLabelIDs: Integerlike[]}) => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    const selectedEventLabelIDs = ref<Integerlike[]>(props.eventSignup.labels.map(v => v.eventSignupLabelID))
    const labelOptionsResolver = ReactiveReifiedPromise<UiOption<Integerlike>[]>(
      async () => {
        return await listEventSignupLabels(axiosAuthBackgroundInstance, {eventID: props.eventID})
          .then(vs => vs.sort(sortBy(v => v.label)))
          .then(vs => vs.map(v => {
            return {
              label: v.label,
              value: v.eventSignupLabelID
            }
          }))
      }
    )

    const submit = () => {
      ctx.emit("submit", {eventLabelIDs: selectedEventLabelIDs.value})
    }

    const cancel = () => {
      ctx.emit("cancel")
    }

    const fname = (v: EventSignup) => {
      return v.userFirstName || v.childFirstName
    }
    const lname = (v: EventSignup) => {
      return v.userLastName || v.childLastName
    }

    return () => {
      return <div>
        <div>
          Add the following labels to {possessivify(`${fname(props.eventSignup)} ${lname(props.eventSignup)}`)} signup:
        </div>
        {(() => {
          switch (labelOptionsResolver.underlying.status) {
            case "idle": return null
            case "error": return <div class="p-2">Sorry, something went wrong.</div>
            case "pending": return <div class="flex p-2 items-center gap-2"><SoccerBall/>Loading...</div>
            case "resolved": {
              const options = labelOptionsResolver.underlying.data
              const ManageLabelsLink = () => <RouterLink class="il-link" to={{name: R_EventLabels.RouteName}}>Manage labels here</RouterLink>

              return <div>
                {options.length === 0
                  ? <div>
                    <p>No labels found.</p>
                    <ManageLabelsLink/>
                  </div>
                  : <div>
                    <FormKit type="checkbox" options={options} v-model={selectedEventLabelIDs.value}/>
                    <ManageLabelsLink/>
                    <div class="flex gap-2 items-center mt-2">
                      <Btn2 class="px-2 py-1" onClick={submit}>Submit</Btn2>
                      <Btn2 class="px-2 py-1" onClick={cancel} enabledClasses={btn2_redEnabledClasses}>Cancel</Btn2>
                    </div>
                  </div>}
              </div>
            }
            default: exhaustiveCaseGuard(labelOptionsResolver.underlying)
          }
        })()}
      </div>

    }
  }
})

export const BulkAddPlayersAsEligibleToRegisterModal = defineComponent({
  props: {
    competitionUID: vReqT<Guid>(),
    players: vReqT<{firstName: string, lastName: string, childID: Guid}[]>(),
  },
  emits: {
    submit: (_: {competitionUID: Guid, seasonUID: Guid, playerIDs: Guid[]}) => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    const competition = lazyRef<Competition>()
    const seasonOptions = lazyRef<UiOptions>()
    const selectedSeasonUID = lazyRef<string>()
    const ready = ref(false);

    onMounted(async () => {
      const seasons = await Client.getSeasons(axiosAuthBackgroundInstance)

      competition.value = await getCompetitionsOrFail({ax: axiosAuthBackgroundInstance, includeDisabled: true, bustCache: true}).then(vs => {
        return requireNonNull(vs.value.find(v => v.competitionUID === props.competitionUID))
      })

      seasonOptions.value = seasons.length === 0
        ? noAvailableOptions()
        : {disabled: false, options: seasons.map(v => ({label: v.seasonName, value: v.seasonUID}))}

      selectedSeasonUID.value = seasonOptions.value.options.find(v => v.value === competition.value.seasonUID)?.value
        || forceCheckedIndexedAccess(seasonOptions.value.options, 0)?.value
        || ""

      ready.value = true
    })

    const submit = () => {
      assertTruthy(selectedSeasonUID.value) // form should have guaranteed this

      ctx.emit("submit", {
        competitionUID: props.competitionUID,
        seasonUID: selectedSeasonUID.value,
        playerIDs: props.players.map(v => v.childID)
      })
    }

    const cancel = () => {
      ctx.emit("cancel")
    }

    const compAdminRouteLoc = computed(() => {
      return R_SeasonCompetitionAdmin.routeDetailToRouteLocation("season-competition-admin.main", {competitionUID: props.competitionUID})
    })

    return () => {
      if (!ready.value) {
        return <div class="flex items-center gap-2"><SoccerBall/>Loading...</div>
      }

      if (competition.value.defaultEligibility) {
        return <div>
          Program '{competition.value.competition}' must have default eligibility disabled in the
          {" "}<RouterLink class="il-link" to={compAdminRouteLoc.value} {...{target:"_blank"}}>Program Administration</RouterLink>
          {" "}before players may be invited.
        </div>
      }

      if (props.players.length === 0) {
        return <div>No players were selected (all selected items were non-player signups).</div>
      }

      return <div style="--fk-margin-outer: none; --fk-padding-input: .35em;">
        <FormKit type="form" actions={false} onSubmit={submit}>
          <div>
            <div>Add the following {props.players.length === 1 ? "player" : `${props.players.length} players`} as eligible for registration:</div>
            <div class="border rounded-md p-2 overflow-y-auto max-h-80" style="--fk-padding-fieldset: 0; --fk-border:none;">
              {props.players.map(v => <div>{v.firstName} {v.lastName}</div>)}
            </div>
          </div>
          <div style="display: grid; grid-template-columns: min-content 1fr;">
            <div class="p-1">Program:</div>
            <div class="p-1">{competition.value.competition}</div>
            <div class="p-1">Season:</div>
            <div class="p-1">
              <FormKit
                type="select"
                placeholder="Select a Season"
                disabled={seasonOptions.value.disabled}
                options={seasonOptions.value.options}
                v-model={selectedSeasonUID.value}
                validation={[["required"]]}
                validationLabel="Season"
              />
            </div>
          </div>

          <div class="mt-4 flex items-center gap-2">
            <Btn2 type="submit" class="px-2 py-1">OK</Btn2>
            <Btn2 class="px-2 py-1" onClick={cancel} enabledClasses={btn2_redEnabledClasses}>Cancel</Btn2>
          </div>
        </FormKit>
      </div>
    }
  }
})

export const BulkSelectByLabelModal = defineComponent({
  props: {
    labels: vReqT<EventLabel[]>(),
  },
  emits: {
    submit: (_: {labels: EventLabel[]}) => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    const selectedEventLabelIDs = MutUiSelection(
      eventLabelIDs => {
        const targets = new Set(eventLabelIDs.map(v => parseIntOrFail(v)));
        return props.labels.filter(v => targets.has(v.eventSignupLabelID))
      },
      [] as string[]
    )

    const options = computed(() : UiOption<Integerlike>[]  => {
      return props.labels.map(v => ({label: v.label, value: v.eventSignupLabelID}))
    })

    const submit = () => {
      ctx.emit("submit", {labels: selectedEventLabelIDs.selectedObj})
    }

    const cancel = () => {
      ctx.emit("cancel")
    }

    return () => {
      return <div style="--fk-margin-outer: none; --fk-padding-input: .35em;">
        {props.labels.length === 0
          ? <div>Current signups listing doesn't have any signups tagged with labels.</div>
          : <>
            <FormKit
              type="checkbox"
              options={options.value}
              v-model={selectedEventLabelIDs.selectedKey}
            />
            <div class="mt-4 flex items-center gap-2">
              <Btn2 class="px-2 py-1" onClick={submit}>OK</Btn2>
              <Btn2 class="px-2 py-1" onClick={cancel} enabledClasses={btn2_redEnabledClasses}>Cancel</Btn2>
            </div>
          </>}
      </div>
    }
  }
})

export const EditSignupCommentsModal = defineComponent({
  props: {
    text: vReqT<string>(),
  },
  emits: {
    submit: (_: {text: string}) => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    const localText = ref(props.text)

    const submit = () => {
      ctx.emit("submit", {text: localText.value})
    }

    const cancel = () => {
      ctx.emit("cancel")
    }

    return () => {
      return <div style="--fk-margin-outer: none; --fk-padding-input: .35em;">
        <textarea v-model={localText.value} class="w-full min-h-48" maxlength="1000" data-test="comments"/>
        <div class="mt-4 flex items-center gap-2">
          <Btn2 class="px-2 py-1" onClick={submit} data-test="submit-button">OK</Btn2>
          <Btn2 class="px-2 py-1" onClick={cancel} enabledClasses={btn2_redEnabledClasses}>Cancel</Btn2>
        </div>
      </div>
    }
  }
})
